import { Avatar, Body2, Button, Caption, IconButton, Image, UIHeading3 } from '@cian/ui-kit';
import { FC, MouseEvent as ReactMouseEvent } from 'react';
import { clsx } from 'clsx';
import { IconActionClose16 } from '@cian/ui-kit-design-tokens/icons';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { ScrollFade } from 'shared/components/ScrollFade';
import { Transport } from 'shared/components/Transport';

import { TGetTgbNativedescGeneralCompleteContextResult } from '../../../../utils/getTgbNativedescGeneralCompleteContext';

import styles from './TgbNativedescGeneralDesktopListingJK.css';

export type TTgbNativedescGeneralDesktopListingJKProps = {
  completeContext: TGetTgbNativedescGeneralCompleteContextResult;
  onClose: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const TgbNativedescGeneralDesktopListingJK: FC<TTgbNativedescGeneralDesktopListingJKProps> = ({
  completeContext: {
    title,
    address,
    description,
    annotation,
    image,
    url,
    button,
    label,
    logoImageUrl,
    builderName,
    transports,
    sizes,
    mortgageTermsWarning,
    mortgageWarning,
  },
  onClose,
}) => {
  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <a
      data-testid="tgb-nativedesc-general-desktop-listing-jk"
      href={url}
      target="_blank"
      className={clsx(styles['container'], withMortgageAnnotations && styles['container--mortgage-annotations'])}
      rel="noopener noreferrer"
      style={sizes}
    >
      <div className={styles['content']}>
        <div className={styles['photo-block']}>
          {!!label && (
            <div data-testid="photo-label" className={styles['photo-label']}>
              <AdLabel label={label} />
            </div>
          )}
          <Image src={image} objectFit="cover" />
        </div>
        <div className={styles['main-content-block']}>
          <div className={styles['content-container']}>
            <UIHeading3 color="text-main-default">{title}</UIHeading3>
            <div>
              {!!transports.length && (
                <div className={styles['transports-container']}>
                  {transports.map(transport => (
                    <Transport
                      key={transport.id}
                      name={transport.name}
                      remoteness={transport.remoteness}
                      iconColor={transport.iconColor}
                      iconType={transport.iconType}
                    />
                  ))}
                </div>
              )}
              <Body2 data-testid="address" color="text-secondary-default">
                {address}
              </Body2>
            </div>
            <div className={styles['description-wrapper']}>
              <Body2 as="p">{description}</Body2>
            </div>
          </div>
          <div>
            <Button data-testid="more-about-jk-button" fullWidth={false} theme="fill_primary" size="XS">
              {button || 'Подробнее о ЖК'}
            </Button>
          </div>
        </div>
        <div className={styles['publisher-block']}>
          <Avatar size={80} src={logoImageUrl} type="builder" />
          <div className={styles['builder-name-wrapper']}>
            <Body2 data-testid="builderName">{builderName}</Body2>
          </div>
          <div className={styles['ad-info']}>
            <Caption color="text-primary-disabled">{annotation}</Caption>
            <ScrollFade size={12} />
          </div>
        </div>
        <div className={styles['close-block']} data-testid="close-block">
          <div>
            <IconButton data-testid="close-button" icon={<IconActionClose16 />} onClick={onClose} />
          </div>
        </div>
      </div>
      {withMortgageAnnotations && (
        <div className={styles['mortgage-annotations']}>
          <MortgageAnnotations
            height={sizes.height}
            mortgageWarning={mortgageWarning}
            mortgageTermsWarning={mortgageTermsWarning}
          />
        </div>
      )}
    </a>
  );
};
